import { Fragment, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Download, Loader2, Send, Tag } from 'lucide-react';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { BillReportScreen } from '../bills/BillReportScreen';
import { MissingTagEditor } from '../billRuns/MissingTagEditor';
import { reportFigures, networkIncomplete, type ReportFigures } from '../../lib/reportStatus';
import type { BillRunRow } from '../../lib/billRunRows';
import { displayRow } from '../../lib/billRunsDisplay';
import type { BillRunsSort, BillRunsSortKey } from '../../lib/billRunsSort';
import { SortableTh } from '../common/SortableTh';
import type { WorkflowStep } from '../../lib/billWorkflow';
import type { BvBill } from '../../types/billTypes';
import { InvoiceLink } from '../bills/InvoiceLink';
import { KeepThisOne } from '../bills/KeepThisOne';
import { AssignedStatusSelect } from './AssignedStatusSelect';

// The Bill Runs table (US-007). One row per bill per month, plus the missing
// site-months. NMI, Entity and Period are real links, so right-click and
// Ctrl/Cmd-click open a new tab. A bill row expands to the customer report,
// with Download PDF and Send to client, as on Bulk Customer Report.

type BillRunsTableProps = {
  rows: readonly BillRunRow[];
  isSelected: (key: string) => boolean;
  onToggle: (key: string) => void;
  /** Header checkbox: first click the page, second click everything matching. */
  headerState: 'none' | 'some' | 'page' | 'all';
  onHeaderClick: () => void;
  savingStep: boolean;
  onSetStep: (billIds: string[], step: WorkflowStep | null) => void;
  onSendOne: (bill: BvBill, figures: ReportFigures) => void;
  onTagsChanged: () => void;
  /** A duplicate was resolved (bills archived): reload. */
  onBillsChanged: () => void;
  /** The column sort shown on the headers; null is the default order. */
  sort: BillRunsSort | null;
  /** A header was clicked. */
  onSort: (key: BillRunsSortKey) => void;
};

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('en-AU') : '—');
const fmtMoney = (n: number | null) =>
  n == null ? '—' : n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 });
const fmtKwh = (n: number | null) => (n == null ? '—' : n.toLocaleString('en-AU', { maximumFractionDigits: 0 }));
const monthLabel = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });

const STATUS_TONE: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  matched: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  mismatched: 'bg-red-50 text-red-700 border-red-200',
};

const HEADERS: Array<{ label: string; key: BillRunsSortKey; numeric?: boolean }> = [
  { label: 'NMI', key: 'nmi' },
  { label: 'Entity', key: 'entity' },
  { label: 'Period', key: 'period' },
  { label: 'Expected total kWh', key: 'kwh', numeric: true },
  { label: 'Retailer', key: 'retailer' },
  { label: 'Tariff', key: 'tariff' },
  { label: 'Expected total', key: 'expected', numeric: true },
  { label: 'Actual total', key: 'actual', numeric: true },
  { label: 'Variance', key: 'variance', numeric: true },
  { label: 'Status', key: 'status' },
  { label: 'Status details', key: 'details' },
  { label: 'Assigned status', key: 'assigned' },
  { label: 'Send status', key: 'send' },
];

export function BillRunsTable(props: BillRunsTableProps) {
  const { rows, isSelected, onToggle, headerState, onHeaderClick } = props;
  const [expanded, setExpanded] = useState<string | null>(null);
  const [tagging, setTagging] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  // The bill, site and entity pages read this for their breadcrumb, so a user
  // goes back to this exact view (tab, filters, page all live in the URL).
  const location = useLocation();
  const backState = { from: { label: 'Bill Runs', to: `${location.pathname}${location.search}` } };

  const download = async (bill: BvBill) => {
    if (!reportRef.current) return;
    setDownloading(true);
    try {
      const { downloadReportAsPdf } = await import('../../utils/pdfUtils');
      const target = (reportRef.current.querySelector('[data-pdf-target]') ?? reportRef.current) as HTMLElement;
      await downloadReportAsPdf(target, `BillValidation_${bill.site?.nmi ?? bill.id}_${fmtDate(bill.bill_from)}.pdf`);
    } catch {
      // silent, as on Bulk Customer Report
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table data-testid="bill-runs2-table" className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b bg-gray-50">
            <th className="pl-3 pr-1 py-2 w-8">
              <input
                data-testid="bill-runs2-select-all"
                type="checkbox"
                aria-label="Select rows: first click this page, second click every matching row"
                title={headerState === 'page' ? 'Click again to select every matching row' : 'Select this page'}
                checked={headerState === 'page' || headerState === 'all'}
                ref={(el) => { if (el) el.indeterminate = headerState === 'some'; }}
                onChange={onHeaderClick}
              />
            </th>
            <th className="w-6" />
            {HEADERS.map((h) => (
              <SortableTh
                key={h.key}
                label={h.label}
                field={h.key}
                sortField={props.sort?.key ?? null}
                sortDir={props.sort?.dir ?? 'asc'}
                onSort={props.onSort}
                className={cn('px-2.5 py-2 font-medium', h.numeric && 'text-right')}
              />
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => {
            const d = displayRow(row);
            const isOpen = expanded === row.key;
            const figures = row.kind === 'bill' && !networkIncomplete(row.board.bill)
              ? reportFigures(row.board.bill) : null;
            return (
              <Fragment key={row.key}>
                <tr
                  data-testid="bill-runs2-row"
                  data-kind={row.kind}
                  data-key={row.key}
                  data-status={d.status.status}
                  className={cn('align-top hover:bg-gray-50', d.kind === 'missing' && d.coverageLabel !== 'Missing' && 'text-gray-400')}
                >
                  <td className="pl-3 pr-1 py-2">
                    <input
                      data-testid="bill-runs2-row-select"
                      type="checkbox"
                      aria-label="Select row"
                      checked={isSelected(row.key)}
                      onChange={() => onToggle(row.key)}
                    />
                  </td>
                  <td className="py-2">
                    {figures && (
                      <button
                        type="button"
                        data-testid="bill-runs2-expand"
                        aria-label={isOpen ? 'Hide report' : 'Show report'}
                        onClick={() => setExpanded(isOpen ? null : row.key)}
                        className="text-gray-400 hover:text-gray-700"
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    )}
                  </td>
                  <td className="px-2.5 py-2 font-mono text-xs whitespace-nowrap">
                    <Link data-testid="bill-runs2-nmi" to={`/bill-validation/sites/${d.siteId}`} state={backState} className="text-blue-700 hover:underline">
                      {d.nmi || '—'}
                    </Link>
                  </td>
                  <td className="px-2.5 py-2 max-w-[12rem] truncate">
                    {d.clientId
                      ? <Link data-testid="bill-runs2-entity" to={`/bill-validation/organisations/${d.clientId}`} state={backState} className="hover:underline">{d.entity || '—'}</Link>
                      : (d.entity || '—')}
                  </td>
                  <td className="px-2.5 py-2 text-xs whitespace-nowrap">
                    {d.billId
                      ? <Link data-testid="bill-runs2-period" to={`/bill-validation/bills/${d.billId}`} state={backState} className="text-blue-700 hover:underline">
                          {fmtDate(d.periodFrom)} – {fmtDate(d.periodTo)}
                        </Link>
                      : <span data-testid="bill-runs2-period">{monthLabel(d.month)}</span>}
                    {row.kind === 'bill' && <InvoiceLink path={row.board.bill.invoice_path} testId="bill-runs2-open-invoice" />}
                    {d.note && <p className="text-[11px] text-gray-500">{d.note}</p>}
                    {d.kind === 'missing' && (
                      <p data-testid="bill-runs2-last-bill" className={cn('text-[11px]', d.lastBill === 'Never' ? 'font-medium text-red-600' : 'text-gray-500')}>
                        {d.lastBill === 'Never' ? 'Never billed' : `Last bill ${fmtDate(d.lastBill)}`}
                      </p>
                    )}
                  </td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{fmtKwh(d.expectedKwh)}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap">{d.retailer || '—'}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap">{d.tariff || '—'}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{fmtMoney(d.expected)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{fmtMoney(d.actual)}</td>
                  <td className={cn(
                    'px-2.5 py-2 text-right tabular-nums',
                    d.status.details === 'overcharge' ? 'text-red-600' : d.status.details === 'undercharge' ? 'text-orange-500' : '',
                  )}>
                    {fmtMoney(d.variance)}
                  </td>
                  <td className="px-2.5 py-2">
                    <span data-testid="bill-runs2-status" className={cn('inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium', STATUS_TONE[d.status.status])}>
                      {d.statusLabel}
                    </span>
                    {row.kind === 'bill' && row.board.twins.count > 0 && (
                      <KeepThisOne bill={row.board.bill} twins={row.board.twins} onDone={props.onBillsChanged} />
                    )}
                  </td>
                  <td data-testid="bill-runs2-details" className="px-2.5 py-2 text-xs whitespace-nowrap">{d.detailLabel || '—'}</td>
                  <td className="px-2.5 py-2">
                    {row.kind === 'bill' ? (
                      <AssignedStatusSelect
                        compact
                        testId="bill-runs2-assigned"
                        value={row.board.bill.workflow_status ?? null}
                        disabled={props.savingStep}
                        onSet={(step) => props.onSetStep([row.board.bill.id], step)}
                      />
                    ) : row.coverage !== 'covered' ? (
                      <button
                        type="button"
                        data-testid="bill-runs2-tag"
                        onClick={() => setTagging(tagging === row.key ? null : row.key)}
                        className="inline-flex items-center gap-1 text-xs text-gray-700 hover:text-blue-700"
                      >
                        <Tag className="h-3 w-3" /> {d.assignedLabel || 'Tag'}
                      </button>
                    ) : null}
                  </td>
                  <td data-testid="bill-runs2-send" className="px-2.5 py-2 text-xs whitespace-nowrap">{d.sendLabel || '—'}</td>
                </tr>

                {row.kind === 'missing' && tagging === row.key && (
                  <tr>
                    <td colSpan={HEADERS.length + 2} className="bg-gray-50 px-4 py-2">
                      <MissingTagEditor
                        cell={{ siteId: row.siteId, month: row.month }}
                        current={row.tag}
                        onDone={(changed) => { setTagging(null); if (changed) props.onTagsChanged(); }}
                      />
                    </td>
                  </tr>
                )}

                {row.kind === 'bill' && isOpen && figures && (
                  <tr>
                    <td colSpan={HEADERS.length + 2} className="border-t border-gray-200 bg-white p-0">
                      <div className="flex justify-end gap-2 px-4 pt-3">
                        <button
                          type="button"
                          data-testid="bill-runs2-download-pdf"
                          onClick={() => void download(row.board.bill)}
                          disabled={downloading}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                        >
                          {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                          {downloading ? 'Generating…' : 'Download PDF'}
                        </button>
                        <button
                          type="button"
                          data-testid="bill-runs2-send-client"
                          onClick={() => props.onSendOne(row.board.bill, figures)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          <Send className="h-3.5 w-3.5" /> Send to client
                        </button>
                      </div>
                      <div data-testid="bill-runs2-inline-report" ref={reportRef}>
                        <BillReportScreen
                          bill={row.board.bill}
                          shadowResult={figures.shadowResult}
                          actualResult={figures.actualResult}
                          gstExcluded={figures.gstExcluded}
                          retailer={row.board.bill.site?.current_retailer ?? undefined}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

